import { SHIP_TO_ASTEROID_CLEARANCE_M, SHIP_TO_SHIP_CLEARANCE_M } from "./worldScale";

/**
 * Ships do not fly through each other (16.100, Don's report).
 *
 * *"Currently ships are allowed to overlap visually in the expedition view, can
 * we keep them from doing this? It makes combat look odd."*
 *
 * Don's rules exactly: ships must not overlap **other ships** or **asteroids**.
 * They may still pass over stations, planets and wrecks -- those read as
 * background the ship is flying above -- and wrecks may overlap each other.
 *
 * ## Where the numbers come from
 *
 * ~~Calibrated to how large a sprite APPEARS at Medium zoom (ship 158m,
 * asteroid 109m), because sprites were sized in pixels and nothing in the game
 * defined a world radius.~~ **SUPERSEDED by 16.101** -- Don asked why the metre
 * scale appeared to move with zoom, which was the right question: the scale never
 * moved, the *sprites* did. `worldScale.ts` now gives every object a real radius
 * in metres and the renderer scales it by zoom, so there is one number for how
 * big a ship is and both the draw path and this module read it.
 *
 * The constants barely moved (340 -> 320 between hulls, 140 -> 270 to a rock) but
 * the reasoning did, and one consequence is real: 270m is further than the
 * player's 150m Move To stop distance, which the old 140m was deliberately
 * chosen to stay inside. 16.101 fixes that at the source instead -- an approach
 * to a solid object now stops at its real surface rather than a flat 150m, so
 * the mover and this module agree by construction instead of by coincidence.
 */

// 16.101 SUPERSEDES the pixel-calibrated constants this section originally
// carried. They are now derived from the real world radii in worldScale.ts, so
// there is exactly one place that says how big a ship is -- the draw path and
// the collision path read the same number. The values barely moved (340 -> 320,
// 140 -> 270), but the reasoning did: 16.100 measured how big a sprite LOOKED at
// one zoom, whereas these are how big the object IS.
export const SHIP_SEPARATION_M = SHIP_TO_SHIP_CLEARANCE_M;
export const ASTEROID_CLEARANCE_M = SHIP_TO_ASTEROID_CLEARANCE_M;
export interface SeparationBody {
  id: string;
  x: number;
  y: number;
  /** False for asteroids: the rock wins, the ship moves. */
  movable: boolean;
  /** Half of the separation this body wants from another of its own kind. */
  radius: number;
}

/**
 * One relaxation pass over every overlapping pair, pushing them apart along the
 * line between their centres.
 *
 * Positional relaxation rather than velocity steering, deliberately. Steering
 * would need the ships' own pursuit AI to know about each other, and a hostile
 * that swerves to avoid a friend is a much larger behaviour change than Don
 * asked for -- he asked them to stop overlapping, not to fly differently. This
 * runs after movement each frame and only nudges what has actually intersected,
 * so pursuit, orbiting and stop distances all still behave exactly as they did.
 *
 * Two bodies at the same position are separated along a deterministic axis
 * rather than a random one -- identical spawn positions are common (a wave
 * spawns on a ring) and a random jitter there would make replays and tests
 * unreproducible.
 *
 * Returns new positions for everything that moved; bodies that did not move are
 * absent from the result, so a caller can cheaply tell whether anything changed.
 */
export function resolveSeparation(bodies: SeparationBody[], iterations = 2): Map<string, { x: number; y: number }> {
  const pos = bodies.map((b) => ({ x: b.x, y: b.y }));
  let moved = false;

  for (let pass = 0; pass < iterations; pass++) {
    for (let i = 0; i < bodies.length; i++) {
      for (let j = i + 1; j < bodies.length; j++) {
        const a = bodies[i];
        const b = bodies[j];
        if (!a.movable && !b.movable) continue;
        const minDist = a.radius + b.radius;
        let dx = pos[j].x - pos[i].x;
        let dy = pos[j].y - pos[i].y;
        let dist = Math.hypot(dx, dy);
        if (dist >= minDist) continue;
        if (dist < 1e-6) {
          // Exactly coincident: pick a stable axis from the pair's identity so
          // the outcome is reproducible rather than random.
          const angle = ((i * 2654435761 + j * 40503) % 628) / 100;
          dx = Math.cos(angle);
          dy = Math.sin(angle);
          dist = 1;
        }
        const overlap = minDist - dist;
        const ux = dx / dist;
        const uy = dy / dist;
        // An immovable body absorbs none of the correction, so the ship takes
        // all of it; two movable bodies split it evenly.
        const aShare = a.movable ? (b.movable ? 0.5 : 1) : 0;
        const bShare = b.movable ? (a.movable ? 0.5 : 1) : 0;
        pos[i].x -= ux * overlap * aShare;
        pos[i].y -= uy * overlap * aShare;
        pos[j].x += ux * overlap * bShare;
        pos[j].y += uy * overlap * bShare;
        moved = true;
      }
    }
    if (!moved) break;
  }

  const result = new Map<string, { x: number; y: number }>();
  bodies.forEach((b, i) => {
    if (pos[i].x !== b.x || pos[i].y !== b.y) result.set(b.id, pos[i]);
  });
  return result;
}
